import Avatar from '@adsk/alloy-react-avatar'
import { ProgressRing } from '../ProgressRing'
import React from 'react'
import styled, { keyframes } from 'styled-components'
import _ from 'lodash'
import type {
	NAPIAvatarProps,
	PlaceHolderProps,
	PlaceHolderContentProps,
	AvatarSize,
} from './types'
import { type ProgressRingSize } from '../ProgressRing'
import Constants from './constants'
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const DefaultProgressRingContainer = styled.div`
	display: flex;
	justify-content: center;
	align-self: center;
	height: 100%;
	width: 100%;
`

const AvatarContainer = styled(Avatar)`
	opacity: 0;
	transition: opacity 0.5s ease-in-out;
	animation: ${fadeIn} 0.5s ease-in-out forwards;
`

/**
 * This component is used to render the NAPI Avatar
 * @param props
 */
const NAPIAvatar: React.FC<NAPIAvatarProps & PlaceHolderProps> = (props) => {
	const [isImageLoaded, setIsImageLoaded] = React.useState<boolean>(false)
	const [imageLoadError, setImageLoadError] = React.useState<boolean>(false)
	let {
		name: avatarName,
		image = '',
		fallbackImage,
		noName = true,
		placeHolder,
		showPlaceHolder,
		style: avatarStyle,
		onError,
		actionComponent,
		...additionalAvatarProps
	} = props
	const {
		type,
		size: placeHolderSize,
		styles: placeHolderStyles,
		...additionalPlaceholderProps
	} = placeHolder as unknown as PlaceHolderContentProps
	// remove default Image and show letter avatar instead - following alloy-avatar standards
	if (image !== null && Constants.DEFAULT_AVATAR_SUFFIX.test(image)) {
		image = ''
	}
	const DefaultAvatarContentLoader = (): JSX.Element | null => {
		return null
	}

	/**
	 * This function is used to render the default static avatar component
	 * @returns JSX.Element
	 */
	const DefaultStaticAvatar = (): JSX.Element => {
		return (
			<Avatar
				size={
					(placeHolderSize as unknown as AvatarSize) ?? Avatar.SIZES.XXLARGE_128
				}
				style={{
					...(placeHolderStyles as React.CSSProperties),
				}}
				name={''}
				{...additionalPlaceholderProps}
			/>
		)
	}

	/**
	 * This function is used to render the placeholder component
	 * If additionalPlaceholderProps.component is provided, then it will render that component
	 * @returns JSX.Element
	 */
	const getPlaceHolderComponent = (): JSX.Element => {
		switch (type) {
			case 'ProgressRing':
				return (
					additionalPlaceholderProps?.component ?? (
						<DefaultProgressRingContainer>
							<ProgressRing
								message={null}
								size={placeHolderSize as unknown as ProgressRingSize}
								style={placeHolderStyles as React.CSSProperties}
							/>
						</DefaultProgressRingContainer>
					)
				)
			case 'ContentLoader':
				return (
					additionalPlaceholderProps?.component ?? (
						<DefaultAvatarContentLoader />
					)
				)
			case 'StaticImage':
				return additionalPlaceholderProps?.component ?? <DefaultStaticAvatar />
			default:
				return <DefaultStaticAvatar />
		}
	}

	/**
	 * This function is used to render the image component
	 * If showPlaceHolder is true, then it will render the placeholder component
	 * @returns JSX.Element
	 */
	const getImageComponent = (): JSX.Element => {
		return (
			<>
				{Boolean(showPlaceHolder) &&
					!isImageLoaded &&
					getPlaceHolderComponent()}
				<AvatarContainer
					style={{
						...(avatarStyle as React.CSSProperties),
						...(showPlaceHolder
							? isImageLoaded
								? { display: 'block' }
								: { display: 'none' }
							: {}),
						...(noName && { backgroundColor: 'transparent' }),
					}}
					data-testid={'avatarContainer'}
					name={noName ? '' : avatarName}
					onLoad={() => {
						Boolean(showPlaceHolder) && setIsImageLoaded(true)
					}}
					onError={(e) => {
						onError?.(e)
						setImageLoadError(true)
					}}
					image={image}
					{...additionalAvatarProps}
				/>
			</>
		)
	}

	// Load Action if the image is loaded or if there is an error in loading the image or if the avatarName is not empty
	const shouldLoadActionComponent = (): boolean => {
		if (_.isEmpty(image) && !_.isEmpty(_.trim(avatarName))) {
			return true
		} else {
			return isImageLoaded || imageLoadError
		}
	}

	/**
	 * This function is used to render the fallback image component
	 * @returns JSX.Element
	 */
	const getFallbackImageComponent = (): JSX.Element => {
		return (
			<AvatarContainer
				{...additionalAvatarProps}
				data-testid={'fallbackAvatarContainer'}
				style={{
					...(avatarStyle as React.CSSProperties),
				}}
				name={!_.isEmpty(_.trim(avatarName)) ? avatarName : ''}
				image={
					_.isEmpty(_.trim(avatarName)) && !_.isEmpty(fallbackImage)
						? fallbackImage
						: ''
				}
				onError={(e) => {
					onError?.(e)
					setImageLoadError(true)
				}}
				onLoad={() => {
					setIsImageLoaded(true)
				}}
			/>
		)
	}

	return (
		<>
			{!_.isEmpty(image)
				? !imageLoadError
					? getImageComponent()
					: getFallbackImageComponent()
				: getFallbackImageComponent()}
			{/* To avoid showing the action component as an individual */}
			{shouldLoadActionComponent() && actionComponent}
		</>
	)
}

NAPIAvatar.displayName = 'NAPIAvatar'
export default NAPIAvatar
